import type { CommandStatus, CommandType, DisplayCommand, PlayerSummary } from '@/types/api'

/** Command lifecycle helpers (PLAYER_PROTOCOL.md §12.1). */

export const TERMINAL_STATUSES: readonly CommandStatus[] = ['completed', 'failed', 'expired', 'canceled']
export const CANCELABLE_STATUSES: readonly CommandStatus[] = ['pending', 'delivered']

export function isTerminal(status: CommandStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}

/** Admin cancel is only allowed from `pending`/`delivered`; anything else is `409 command-state-conflict`. */
export function canCancel(status: CommandStatus): boolean {
  return CANCELABLE_STATUSES.includes(status)
}

export function hasActiveCommands(commands: readonly Pick<DisplayCommand, 'status'>[] | undefined): boolean {
  return !!commands?.some((c) => !isTerminal(c.status))
}

export type CommandTone = 'muted' | 'info' | 'primary' | 'success' | 'danger' | 'offline'

export const COMMAND_STATUS_META: Record<CommandStatus, { label: string; tone: CommandTone; live: boolean }> = {
  pending: { label: 'Pending', tone: 'muted', live: true },
  delivered: { label: 'Delivered', tone: 'info', live: true },
  acknowledged: { label: 'Acknowledged', tone: 'info', live: true },
  started: { label: 'Running', tone: 'primary', live: true },
  completed: { label: 'Completed', tone: 'success', live: false },
  failed: { label: 'Failed', tone: 'danger', live: false },
  expired: { label: 'Expired', tone: 'offline', live: false },
  canceled: { label: 'Canceled', tone: 'offline', live: false },
}

export const COMMAND_LABELS: Record<CommandType, string> = {
  sync: 'Sync now',
  reload: 'Reload content',
  restart_renderer: 'Restart renderer',
  restart_player: 'Restart player',
  reboot_device: 'Reboot device',
  clear_cache: 'Clear cache',
  screenshot: 'Screenshot',
  diagnostics: 'Collect diagnostics',
  update_player: 'Update player',
  rotate_credentials: 'Rotate credentials',
  identify: 'Identify',
  start_setup: 'Start setup Wi-Fi',
}

/** Label for a command type; `start_setup` reads differently per mode (`ap` Wi-Fi vs. LAN `pin`). */
export function commandLabel(type: string, payload?: Record<string, unknown> | null): string {
  if (type === 'start_setup' && payload?.mode === 'pin') return 'Show setup PIN'
  return (COMMAND_LABELS as Record<string, string>)[type] ?? type.replace(/_/g, ' ')
}

/** Human summary of a completed `start_setup` result (`{mode}`, PLAYER_PROTOCOL.md §12.2). */
export function startSetupSummary(mode: unknown): string | null {
  if (mode === 'ap') return 'Setup Wi-Fi is on — name and password on the screen'
  if (mode === 'lan') return 'Setup PIN shown on the screen'
  if (mode === 'unsupported') return 'Setup isn’t available on this device'
  return null
}

/** Commands that interrupt playback or the device and need an explicit confirmation. */
export const DESTRUCTIVE_COMMANDS: readonly CommandType[] = ['restart_player', 'reboot_device', 'rotate_credentials', 'clear_cache']

export function needsConfirmation(type: CommandType): boolean {
  return DESTRUCTIVE_COMMANDS.includes(type)
}

/**
 * Newer commands only players that advertise them accept (the server refuses the rest with
 * `409 command-unsupported`), so unknown capabilities do not enable them.
 */
export const ADVERTISED_ONLY_COMMANDS: readonly CommandType[] = ['start_setup']

/**
 * Whether the player can execute `type` (PLAYER_PROTOCOL.md §11 `capabilities.commands`).
 * Unknown capabilities (older players) allow everything except {@link ADVERTISED_ONLY_COMMANDS};
 * consumer Tizen / webOS TVs, for example, report neither `reboot_device` nor `screenshot`.
 */
export function commandSupported(player: Pick<PlayerSummary, 'supported_commands'> | null | undefined, type: CommandType): boolean {
  const list = player?.supported_commands
  if (!Array.isArray(list)) return !ADVERTISED_ONLY_COMMANDS.includes(type)
  return list.includes(type)
}

/** Most relevant timestamp for a command row. */
export function commandTimestamp(c: DisplayCommand): string {
  return c.completed_at ?? c.started_at ?? c.acknowledged_at ?? c.delivered_at ?? c.created_at
}
